import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import JobMarketplace from './pages/job-marketplace';
import DirectJobApply from './pages/job-marketplace/DirectJobApply';
import ClientPortal from './pages/client-portal';
import ServicePortfolioHub from './pages/service-portfolio-hub';
import SuccessStoriesShowcase from './pages/success-stories-showcase';
import GlobalTradeNetwork from './pages/global-trade-network';
import Homepage from './pages/homepage';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import { Navigate } from 'react-router-dom';
import { AuthRouteSync } from './contexts/AuthContext';

// ERP System imports
import ERPLogin from './pages/erp/Login';
import { ProtectedRoute, RoleBasedRoute } from './components/ProtectedRoute';

// Import ERP pages from src/ using @erp alias (defined in vite.config.mjs)
// Temporary solution - copy to www/src/pages/erp/ for production
import DashboardOverview from '@erp/pages/dashboard-overview';
import EmployeeManagement from '@erp/pages/employee-management';
import PayrollManagement from '@erp/pages/payroll-management';
import InvoiceBillingManagement from '@erp/pages/invoice-billing-management';
import ClientManagement from '@erp/pages/client-management';
import EmployeeSelfService from '@erp/pages/employee-self-service';
import TelecallerDashboard from '@erp/pages/telecaller-dashboard';
import ApplicantTrackingSystem from '@erp/pages/applicant-tracking-system';
import ClientRequirementsMatching from '@erp/pages/client-requirements-matching';
import ComplianceStatutoryReports from '@erp/pages/compliance-statutory-reports';
import Settings from '@erp/pages/settings';
import CompanyConfig from '@erp/pages/company-config';
import HumanResources from '@erp/pages/hr';
import ClientSiteManagement from '@erp/pages/client-site-management';
import EmployeeDeploymentManagement from '@erp/pages/employee-deployment-management';
import CRMLeadManagement from '@erp/pages/crm-lead-management';
import SelfiePunchIn from '@erp/pages/selfie-attendance/punch-in';
import SelfiePunchOut from '@erp/pages/selfie-attendance/punch-out';
import AiInterviewLayout from '@erp/pages/ai-interview/AiInterviewLayout';
import AiInterviewDashboard from '@erp/pages/ai-interview/Dashboard';
import InterviewSetup from '@erp/pages/ai-interview/InterviewSetup';
import InterviewSession from '@erp/pages/ai-interview/InterviewSession';
import InterviewResults from '@erp/pages/ai-interview/InterviewResults';
import ResumeAnalyzer from "@erp/pages/ai-interview/ResumeAnalyzer";
import VoiceInterview from "@erp/pages/ai-interview/VoiceInterview";
import LifecycleDashboard from '@erp/pages/lifecycle-dashboard';
import PublicWalkinApply from './pages/walkin-apply/PublicWalkinApply';
import WalkinDrivesPage from '@erp/pages/walkin-drives';
import WalkinPanelDashboard from '@erp/pages/walkin-drives/WalkinPanelDashboard';
import HrScreeningQuestionnaire from '@erp/pages/hr-screening/HrScreeningQuestionnaire';
import AttendanceManagement from '@erp/pages/attendance-management';
import AttendanceTracker from '@erp/pages/attendance-tracker/AttendanceTracker';
import LiveAttendance from '@erp/pages/live-attendance/LiveAttendance';
import PunchPage from '@erp/pages/selfie-attendance/PunchPage';
import EmployeeLogin from './pages/employee-auth/EmployeeLogin';
import EmployeeSetupPassword from './pages/employee-auth/EmployeeSetupPassword';
import EmployeePortalLayout from './pages/employee-portal/EmployeePortalLayout';

// Simple ProtectedRoute for client-portal (uses localStorage)
const ClientPortalRoute = ({ children }) => {
  const user = localStorage.getItem('user');
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AuthRouteSync />
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* ============================================
              PUBLIC WEBSITE ROUTES (No Authentication)
              ============================================ */}
          <Route path="/" element={<Homepage />} />
          <Route path="/homepage" element={<Homepage />} />
          <Route path="/job-marketplace" element={<JobMarketplace />} />
          <Route path="/apply/:jobId" element={<DirectJobApply />} />
          <Route path="/job/:jobId" element={<DirectJobApply />} />
          <Route path="/job/:jobId/apply" element={<DirectJobApply />} />
          <Route path="/ai-screening/:applicationId" element={<VoiceInterview />} />
          <Route path="/hr-screening/:id" element={<HrScreeningQuestionnaire />} />
          <Route path="/walkin-apply/:token" element={<PublicWalkinApply />} />
          <Route path="/service-portfolio-hub" element={<ServicePortfolioHub />} />
          <Route path="/success-stories-showcase" element={<SuccessStoriesShowcase />} />
          <Route path="/global-trade-network" element={<GlobalTradeNetwork />} />

          {/* Public Auth Routes (for client portal) */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Legacy ERP login routes */}
          <Route path="/login-authentication" element={<Navigate to="/erp/login" replace />} />
          <Route path="/erp/login-authentication" element={<Navigate to="/erp/login" replace />} />

          {/* Client Portal (uses simple localStorage auth) */}
          <Route
            path="/client-portal"
            element={
              <ClientPortalRoute>
                <ClientPortal />
              </ClientPortalRoute>
            }
          />

          {/* ============================================
              ERP SYSTEM ROUTES (Requires Authentication)
              All routes under /erp/* prefix
              ============================================ */}

          {/* ERP Login */}
          <Route path="/erp/login" element={<ERPLogin />} />

          {/* ERP Protected Routes */}
          <Route
            path="/erp/dashboard-overview"
            element={
              <ProtectedRoute>
                <DashboardOverview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/erp/lifecycle-dashboard"
            element={
              <ProtectedRoute>
                <LifecycleDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/erp/employee-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <EmployeeManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/payroll-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <PayrollManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/attendance-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <AttendanceManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/attendance-tracker"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <AttendanceTracker />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/live-attendance"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <LiveAttendance />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/attendance/punch"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <PunchPage />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/attendance/punch/:employeeId"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <PunchPage />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/selfie-attendance"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <PunchPage />
              </RoleBasedRoute>
            }
          />

          {/* Employee Self-Service Routes */}
          <Route path="/employee/login" element={<EmployeeLogin />} />
          <Route path="/employee/setup-password" element={<EmployeeSetupPassword />} />
          <Route path="/employee/forgot-password" element={<EmployeeSetupPassword defaultMode="forgot" />} />
          <Route path="/employee/portal/*" element={<EmployeePortalLayout />} />

          <Route
            path="/erp/invoice-billing-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'client']}>
                <InvoiceBillingManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/client-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <ClientManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/employee-self-service/*"
            element={
              <ProtectedRoute>
                <EmployeeSelfService />
              </ProtectedRoute>
            }
          />

          <Route
            path="/erp/telecaller-dashboard"
            element={
              <RoleBasedRoute allowedRoles={['telecaller', 'super_admin']}>
                <TelecallerDashboard />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/applicant-tracking-system/*"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <ApplicantTrackingSystem />
              </RoleBasedRoute>
            }
          />

          {/* Walk-in Hiring Drives Routes */}
          <Route
            path="/erp/walkin-drives"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <WalkinDrivesPage />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/erp/walkin-drives/:id/panel"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <WalkinPanelDashboard />
              </RoleBasedRoute>
            }
          />
          <Route
            path="/erp/walkin-panel-dashboard"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <WalkinPanelDashboard />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/client-requirements-matching"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'staff']}>
                <ClientRequirementsMatching />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/compliance-statutory-reports"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <ComplianceStatutoryReports />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/settings"
            element={
              <RoleBasedRoute allowedRoles={['super_admin']}>
                <Settings />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/company-config/*"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <CompanyConfig />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/hr/*"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <HumanResources />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/client-site-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <ClientSiteManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/employee-deployment-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin']}>
                <EmployeeDeploymentManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/crm-lead-management"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'telecaller']}>
                <CRMLeadManagement />
              </RoleBasedRoute>
            }
          />

          <Route
            path="/erp/selfie-attendance/punch-in"
            element={
              <ProtectedRoute>
                <SelfiePunchIn />
              </ProtectedRoute>
            }
          />

          <Route
            path="/erp/selfie-attendance/punch-out"
            element={
              <ProtectedRoute>
                <SelfiePunchOut />
              </ProtectedRoute>
            }
          />

          <Route
            path="/erp/ai-interview"
            element={
              <RoleBasedRoute allowedRoles={['super_admin', 'hr_admin', 'staff']}>
                <AiInterviewLayout />
              </RoleBasedRoute>
            }
          >
            <Route index element={<AiInterviewDashboard />} />
            <Route path="setup" element={<InterviewSetup />} />
            <Route path="session" element={<InterviewSession />} />
            <Route path="results" element={<InterviewResults />} />
	    <Route path="resume" element={<ResumeAnalyzer />} />
      <Route path="voice" element={<VoiceInterview />} />
          </Route>

          {/* Redirect /erp to login */}
          <Route path="/erp" element={<Navigate to="/erp/login" replace />} />

          {/* 404 - Must be last */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
